import { Table, Typography, styled } from '@material-ui/core';
import { ParagraphText } from './ParagraphText';

const FONT = { display: 'Red Hat Display', body: 'Open Sans' } as const;
const FONT_SIZE = {
  body: 14,
  bodyLarge: 16,
  h1: 40,
} as const;
const bodyText = {
  fontFamily: FONT.body,
  fontSize: FONT_SIZE.body,
  lineHeight: '22px',
} as const;

export const SectionTitle = styled(Typography)({
  fontFamily: FONT.display,
  fontWeight: 700,
  marginBottom: 8,
});

export const BulletList = styled('ul')(({ theme }) => ({
  paddingLeft: 16,
  '& li::marker': { color: theme.palette.primary.main },
}));

export const BulletItem = styled('li')({
  ...bodyText,
  marginBottom: 12,
});

export const MarkdownDocTitle = styled('h1')(({ theme }) => ({
  fontFamily: FONT.display,
  fontWeight: 700,
  fontSize: FONT_SIZE.h1,
  color: theme.palette.text.primary,
  margin: '0 0 8px 0',
}));

export const MarkdownDocParagraph = styled(Typography)(({ theme }) => ({
  fontFamily: FONT.body,
  fontSize: FONT_SIZE.bodyLarge,
  lineHeight: 1.5,
  color: theme.palette.text.primary,
  marginBottom: 16,
}));

export const MarkdownDocTable = styled(Table)(({ theme }) => ({
  fontFamily: FONT.body,
  fontSize: FONT_SIZE.bodyLarge,
  marginBottom: 24,
  '& .MuiTableCell-root': {
    border: `1px solid ${theme.palette.divider}`,
    padding: '12px 16px',
  },
  '& .MuiTableCell-head': {
    fontWeight: 700,
    backgroundColor: theme.palette.action.hover,
  },
  '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)': {
    backgroundColor: theme.palette.action.hover,
  },
}));

export const MarkdownInlineCode = styled('code')(({ theme }) => ({
  fontFamily:
    'Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  fontSize: 14,
  lineHeight: '20px',
  borderRadius: 3,
  padding: '2px 6px',
  color: theme.palette.text.primary,
  backgroundColor: '#EEF1F6',
  border: `1px solid ${theme.palette.divider}`,
  display: 'inline',
  verticalAlign: 'baseline',
}));

export const MarkdownCodeBlockContainer = styled('div')(({ theme }) => ({
  marginBottom: 24,
  borderRadius: 6,
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
  backgroundColor: '#F7F8FA',
}));

export const MarkdownCodeHeader = styled('div')(({ theme }) => ({
  height: 30,
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  padding: '0 10px',
}));

export const MarkdownCodeCopyIcon = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  color: theme.palette.text.secondary,
  lineHeight: 0,
}));

export const MarkdownCodeBody = styled('div')({
  overflowX: 'auto',
  padding: '10px 0',
});

export const MarkdownCodeLine = styled('div')({
  display: 'grid',
  gridTemplateColumns: '36px 1fr',
  alignItems: 'start',
  gap: 8,
  minHeight: 24,
  padding: '0 12px',
});

export const MarkdownCodeLineNumber = styled('span')(({ theme }) => ({
  fontFamily:
    'Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  fontSize: 14,
  lineHeight: '24px',
  color: theme.palette.text.secondary,
  userSelect: 'none',
  textAlign: 'right',
}));

export const MarkdownCodeLineText = styled('code')({
  fontFamily:
    'Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  fontSize: 14,
  lineHeight: '24px',
  color: '#2F4F34',
  whiteSpace: 'pre',
});

export const MarkdownAdmonition = styled('section')(({ theme }) => ({
  margin: '0 0 20px 0',
  borderRadius: 6,
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
  backgroundColor: theme.palette.background.default,
  '&[data-variant="warning"]': {
    borderColor: theme.palette.warning.main,
  },
  '&[data-variant="tip"]': {
    borderColor: theme.palette.success.main,
  },
  '&[data-variant="danger"]': {
    borderColor: theme.palette.error.main,
  },
  '&[data-variant="info"]': {
    borderColor: theme.palette.primary.main,
  },
}));

export const MarkdownAdmonitionHeader = styled('div')(({ theme }) => ({
  minHeight: 42,
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '0 14px',
  '&[data-variant="warning"]': {
    backgroundColor: theme.palette.warning.light,
  },
  '&[data-variant="tip"]': {
    backgroundColor: theme.palette.success.light,
  },
  '&[data-variant="danger"]': {
    backgroundColor: theme.palette.error.light,
  },
  '&[data-variant="info"]': {
    backgroundColor: theme.palette.primary.light,
  },
}));

export const MarkdownAdmonitionIcon = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  lineHeight: 0,
  '&[data-variant="warning"]': {
    color: theme.palette.warning.dark,
  },
  '&[data-variant="tip"]': {
    color: theme.palette.success.dark,
  },
  '&[data-variant="danger"]': {
    color: theme.palette.error.dark,
  },
  '&[data-variant="info"]': {
    color: theme.palette.primary.main,
  },
}));

export const MarkdownAdmonitionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: FONT.display,
  fontWeight: 700,
  fontSize: FONT_SIZE.bodyLarge,
  color: theme.palette.text.primary,
}));

export const MarkdownAdmonitionBody = styled('div')({
  padding: '14px',
  '& > :last-child': {
    marginBottom: 0,
  },
});

export { ParagraphText };
